import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/sv";
import BaseTable, { IBaseTableData, IBaseTableRow } from "./table/baseTable";
import { FetchRequestTypes, useFetch } from "../hooks/useFetch";
import { t } from "i18next";
import LoadMoreButton from "./btnLoadMore";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
export interface ICruiseCallsProps {
  show: boolean;
}

export interface CruiseData {
  ship: string;
  arrival: string;
  departure: string;
  harbour: string;
  shippingCompany: string;
  agent: string;
  arrivedFrom: string;
  departTo: string;
  quay: string;
  length: string;
  depth: string;
  noPax: string;
  gt: string;
  shipImageUrl: string;
  arrivalChanged: string;
  departureChanged: string;
  cancelled: boolean;
  note: string;
}

export interface CruiseDetailsProps {
  cruiseDetails: CruiseData;
  expanded: boolean;
}

export interface ShipImageProps {
  url: string | null;
  show: boolean;
}

// --------------------------------------
//  Ship image
// --------------------------------------

const ShipImage = ({ url, show }: ShipImageProps) => {
  const imageFallbackUrl = "/Assets/images/hero_2880x1264_kajplatser.jpg";
  const [loaded, setLoaded] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (url && show) {
      setImageUrl(url);
    }
  }, [url, show]);

  useEffect(() => {
    if (imageUrl) {
      let image = new Image();
      image.src = imageUrl;

      image.onload = () => {
        setLoaded(true);
      };

      image.onerror = () => {
        setImageUrl(imageFallbackUrl);
      };
    }
  }, [imageUrl]);

  return imageUrl ? (
    <div
      className={`cruise-ship-image ${loaded && show ? "show" : ""}`}
      style={{ backgroundImage: `url(${imageUrl})` }}
    ></div>
  ) : null;
};

const TableHeader = (headerText: string, className?: string) => (
  <span className={className}>{headerText}</span>
);
const RowColumn = (columnText: string, classNames?: string[]) => (
  <div className={classNames ? classNames.join(" ") : ""}>{columnText}</div>
);

const getDateWithTimeZone = (date: string, format: string) => {
  if (!date.length) {
    return "";
  }

  return dayjs(new Date(date)).tz("Europe/Stockholm", true).format(format);
};

const ExpandedCruiseDetails = ({
  cruiseDetails,
  expanded,
}: CruiseDetailsProps) => {
  const arrivalDateWithTimeZone = getDateWithTimeZone(
    cruiseDetails.arrival,
    "DD MMM HH:mm",
  );
  const departureDateWithTimeZone = getDateWithTimeZone(
    cruiseDetails.departure,
    "DD MMM HH:mm",
  );

  const arrivalChangedDateWithTimeZone = getDateWithTimeZone(
    cruiseDetails.arrivalChanged,
    "DD MMM HH:mm",
  );
  const departureChangedDateWithTimeZone = getDateWithTimeZone(
    cruiseDetails.departureChanged,
    "DD MMM HH:mm",
  );

  return (
    <div className="cruise-details">
      <div className="cruise-details-content">
        <div className="cruise-details-top">
          <div className="column-left">
            <ul>
              <li>
                <h6>{t("cruisingpage.arrival")}:</h6>
                {arrivalChangedDateWithTimeZone ? (
                  <div>
                    <p style={{ fontStyle: "italic" }}>
                      {arrivalChangedDateWithTimeZone}
                    </p>
                    <br />
                    <span style={{ textDecoration: "line-through" }}>
                      {arrivalDateWithTimeZone}
                    </span>
                  </div>
                ) : (
                  <p>{arrivalDateWithTimeZone}</p>
                )}
              </li>
              <li>
                <h6>{t("cruisingpage.arrivalfrom")}:</h6>
                <p>{cruiseDetails.arrivedFrom}</p>
              </li>
            </ul>
          </div>
          <div className="column-right">
            <ul>
              <li>
                <h6>{t("cruisingpage.departure")}:</h6>
                {departureChangedDateWithTimeZone ? (
                  <div>
                    <p style={{ fontStyle: "italic" }}>
                      {departureChangedDateWithTimeZone}
                    </p>
                    <br />
                    <span style={{ textDecoration: "line-through" }}>
                      {departureDateWithTimeZone}
                    </span>
                  </div>
                ) : (
                  <p>{departureDateWithTimeZone}</p>
                )}
              </li>
              <li>
                <h6>{t("cruisingpage.departureto")}:</h6>
                <p>{cruiseDetails.departTo}</p>
              </li>
            </ul>
          </div>
        </div>
        <div className="cruise-details-bottom">
          <div className="column-left">
            <ul>
              <li className="mobile-only">
                <h6>{t("cruisingpage.quaypart")}:</h6>
                <p>{cruiseDetails.harbour}</p>
              </li>
              <li>
                <h6>{t("cruisingpage.quay")}:</h6>
                <p>{cruiseDetails.quay}</p>
              </li>
              <li>
                <h6>{t("cruisingpage.length")}:</h6>
                <p>{cruiseDetails.length}</p>
              </li>
              <li>
                <h6>{t("cruisingpage.depth")}:</h6>
                <p>{cruiseDetails.depth}</p>
              </li>
            </ul>
          </div>
          <div className="column-right">
            <ul>
              <li className="mobile-only">
                <h6>{t("cruisingpage.shippingcompany")}:</h6>
                <p>{cruiseDetails.shippingCompany}</p>
              </li>
              <li className="mobile-only">
                <h6>{t("cruisingpage.agent")}:</h6>
                <p>{cruiseDetails.agent}</p>
              </li>
              <li>
                <h6>{t("cruisingpage.nopax")}:</h6>
                <p>{cruiseDetails.noPax}</p>
              </li>
              <li>
                <h6>{t("cruisingpage.gt")}:</h6>
                <p>{cruiseDetails.gt}</p>
              </li>
            </ul>
          </div>
        </div>
        {cruiseDetails.note ? (
          <div className="cruise-details-note">{cruiseDetails.note}</div>
        ) : null}
      </div>
      <ShipImage url={cruiseDetails.shipImageUrl} show={expanded} />
    </div>
  );
};

const CruiseCalls = ({ show }: ICruiseCallsProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string>();
  const [locale, setLocale] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const [apiUrl, setApiUrl] = useState<string | null>(null);
  const { data, error, loading } = useFetch(apiUrl, FetchRequestTypes.POST);
  const [cruiseData, setCruiseData] = useState<CruiseData[]>();
  const [allCruiseData, setAllCruiseData] = useState<CruiseData[]>();

  const [loadMore, setLoadMore] = useState(false);

  const itemsToLoad = 1;

  //if showAllData is true then the whole list of ships will me shown
  const showAllData = true;

  useEffect(() => {
    const e = document.getElementById("cruiseCalls");
    if (e?.dataset.pageName) {
      setUrl(e.dataset.pageName);
    }

    if (e?.dataset.pageLocale) {
      setLocale(e.dataset.pageLocale);
    }
  }, []);

  useEffect(() => {
    if (data) {
      setAllCruiseData(data);
      if (showAllData) {
        setCruiseData(data);
      } else {
        setCruiseData((data as CruiseData[]).slice(0, itemsToLoad));
      }
    }
  }, [data]);

  useEffect(() => {
    if (loadMore && cruiseData) {
      const currentIndex = cruiseData.length;
      const newItems = allCruiseData?.slice(
        currentIndex,
        currentIndex + itemsToLoad,
      );
      if (newItems) {
        setCruiseData([...cruiseData, ...newItems]);
      }
      setLoadMore(false);
    }
  }, [loadMore]);

  useEffect(() => {
    if (locale) {
      dayjs.locale(locale);
      const pathName = window.location.href;
      setApiUrl(`${pathName}GetCruisings`);
    }
  }, [locale]);

  const { t } = useTranslation();
  const tableData = useMemo(() => {
    const baseData: IBaseTableData = {
      headers: [
        { id: 1, headerData: TableHeader(t("cruisingpage.ship")), columnId: 1 },
        {
          id: 2,
          headerData: TableHeader(t("cruisingpage.arrival")),
          columnId: 2,
        },
        {
          id: 3,
          headerData: TableHeader(t("cruisingpage.departure")),
          columnId: 3,
        },
        {
          id: 4,
          headerData: TableHeader(t("cruisingpage.quaypart")),
          hideOnMobile: true,
          columnId: 4,
        },
        {
          id: 5,
          headerData: TableHeader(t("cruisingpage.shippingcompany")),
          hideOnMobile: true,
          columnId: 5,
        },
        {
          id: 6,
          headerData: TableHeader(t("cruisingpage.agent")),
          hideOnMobile: true,
          columnId: 6,
        },
      ],
      rows: [],
    };

    cruiseData?.forEach((data, index) => {
      const realArrival = data.arrivalChanged
        ? data.arrivalChanged
        : data.arrival;
      const realDeparture = data.departureChanged
        ? data.departureChanged
        : data.departure;

      const rowData: IBaseTableRow = {
        id: index,
        columns: [
          {
            id: 1,
            columnData: RowColumn(data.ship, [
              "cruise-table-column",
              data.cancelled ? "cancelled" : "",
            ]),
            filterValue: data.ship,
          },
          {
            id: 2,
            columnData: RowColumn(dayjs(realArrival).format("DD MMMM"), [
              "cruise-table-column",
              data.arrivalChanged ? "date-changed" : "",
            ]),
            filterValue: dayjs().valueOf() - dayjs(realArrival).valueOf(),
          },
          {
            id: 3,
            columnData: RowColumn(dayjs(realDeparture).format("DD MMMM"), [
              "cruise-table-column",
              data.departureChanged ? "date-changed" : "",
            ]),
            filterValue: dayjs().valueOf() - dayjs(realDeparture).valueOf(),
          },
          {
            id: 4,
            columnData: RowColumn(data.harbour),
            hideOnMobile: true,
            filterValue: data.harbour,
          },
          {
            id: 5,
            columnData: RowColumn(data.shippingCompany),
            hideOnMobile: true,
            filterValue: data.shippingCompany,
          },
          {
            id: 6,
            columnData: RowColumn(data.agent),
            hideOnMobile: true,
            filterValue: data.shippingCompany,
          },
        ],

        expandedContent: (
          <ExpandedCruiseDetails
            cruiseDetails={data}
            expanded={expandedRows.some((r) => r === index)}
          />
        ),
        expandedAtStart: false,
      };

      baseData?.rows.push(rowData);
    });
    return baseData;
  }, [cruiseData, JSON.stringify(expandedRows)]);

  const onRowExpanded = (row: IBaseTableRow) => {
    expandedRows.push(row.id);
    setExpandedRows([...expandedRows]);
  };

  const onRowClosed = (row: IBaseTableRow) => {
    const i = expandedRows.findIndex((rowId) => rowId === row.id);
    if (i >= 0) {
      expandedRows.splice(i, 1);
      setExpandedRows([...expandedRows]);
    }
  };

  const loadMoreItems = () => {
    setLoadMore(true);
  };

  return (
    <div ref={ref} className="cruise-calls">
      <BaseTable
        data={tableData}
        onExpanded={(row: IBaseTableRow) => onRowExpanded(row)}
        onClosed={(row: IBaseTableRow) => onRowClosed(row)}
      />
      {allCruiseData &&
      cruiseData &&
      cruiseData?.length < allCruiseData?.length ? (
        <div className="cruise-calls-load-more">
          <LoadMoreButton
            className="cruise-calls-load-more-button"
            text={
              loadMore
                ? t("arrivalsdepartures.loading")
                : t("arrivalsdepartures.loadMore")
            }
            onClick={() => loadMoreItems()}
            isLoading={loadMore}
          />
        </div>
      ) : null}
    </div>
  );
};

export default CruiseCalls;
